import { ViewBooking, ViewBookingAssignedAreasInner } from "collins-js-sdk";
import React from "react";
import {
  BookingType,
  BookingZone,
  VenueConfig
} from "../../../../../types/venues";
import {
  afternoonFilter,
  autoConfirmedFilter,
  collinsCallChanneFilter,
  customerRequestsFilter,
  doubleConfirmedFilter,
  eveningFilter,
  morningFilter,
  notDoubleConfirmedFilter,
  runSheetNotesFilter
} from "../../../../../utils/bookingFilters";
import { ISelectedFilters } from "./DiaryFilters";

export type DiaryFilterSectonsProp = {
  title: string;
  key: FilterSectionKeys;
  options: DiaryFilterSectonOptionsProp[];
  maxSelections?: number;
};

export type DiaryFilterSectonOptionsProp = {
  key: string;
  title: string;
  filter: (booking: ViewBooking) => boolean | "" | null | undefined;
  isSelected: boolean;
};

export const getinitialFilterQueryMapping = (venueConfig: VenueConfig) =>
  getDefaultDiaryFilterSections(venueConfig).reduce(
    (objToCreate: any, section: any) => {
      objToCreate[section.key] = new Set();
      return objToCreate;
    },
    {}
  );

export enum FilterSectionKeys {
  timeOfDay = "timeOfDay",
  bookingTypes = "bookingTypes",
  bookingZones = "bookingZones",
  bookingAttributes = "bookingAttributes"
}
export const getDefaultDiaryFilterSections = (
  venueConfig: VenueConfig | null
): DiaryFilterSectonsProp[] => {
  return [
    {
      title: "",
      key: FilterSectionKeys.timeOfDay,
      maxSelections: 999,
      options: [
        {
          key: "morning",
          title: "Morning",
          filter: morningFilter,
          isSelected: false
        },
        {
          key: "afternoon",
          title: "Afternoon",
          filter: afternoonFilter,
          isSelected: false
        },
        {
          key: "evening",
          title: "Evening",
          filter: eveningFilter,
          isSelected: false
        }
      ]
    },
    {
      title: "Booking types",
      key: FilterSectionKeys.bookingTypes,
      maxSelections: 999,
      options: venueConfig?.booking_types
        ? venueConfig.booking_types
            .filter((bookingType: BookingType) => !bookingType.archived)
            .map((bookingType: BookingType) => {
              return {
                key: bookingType.id,
                title: bookingType.name,
                filter: (booking: ViewBooking) =>
                  booking.type?.id === bookingType.id,
                isSelected: false
              };
            })
        : []
    },
    {
      title: "Zones",
      key: FilterSectionKeys.bookingZones,
      maxSelections: 999,
      options: venueConfig?.bookable_area_zones
        ? venueConfig.bookable_area_zones.map((bookingZone: BookingZone) => {
            const zoneId = bookingZone.id || "";
            return {
              key: zoneId,
              title: bookingZone.name || "",
              filter: (booking: ViewBooking) => {
                if (!booking.assignedAreas) return false;
                return booking.assignedAreas.some(
                  (assignArea: ViewBookingAssignedAreasInner) =>
                    assignArea.zone?.toString() === zoneId
                );
              },
              isSelected: false
            };
          })
        : []
    },
    {
      title: "Booking attributes",
      key: FilterSectionKeys.bookingAttributes,
      options: [
        {
          key: "autoConfirmed",
          title: "Auto confirmed",
          filter: autoConfirmedFilter,
          isSelected: false
        },
        {
          key: "customerRequests",
          title: "Customer requests",
          filter: customerRequestsFilter,
          isSelected: false
        },
        {
          key: "runSheetNotes",
          title: "Run sheet notes",
          filter: runSheetNotesFilter,
          isSelected: false
        },
        {
          key: "doubleConfirmed",
          title: "Double Confirmed",
          filter: doubleConfirmedFilter,
          isSelected: false
        },
        {
          key: "notDoubleConfirmed",
          title: "Not Double confirmed",
          filter: notDoubleConfirmedFilter,
          isSelected: false
        },
        {
          key: "collinsCall",
          title: "Collins Call Booking",
          filter: collinsCallChanneFilter,
          isSelected: false
        }
      ]
    }
  ];
};

export interface IUserAppliedFilterKeys {
  [index: string]: Set<string>;
}
interface IToggleFilter {
  sectionKey: FilterSectionKeys;
  optionKey: string;
  userAppliedFilterKeys: IUserAppliedFilterKeys;
  diaryFilterSections: DiaryFilterSectonsProp[];
  setUserAppliedFilterKeys: React.Dispatch<
    React.SetStateAction<IUserAppliedFilterKeys>
  >;
}
export const toggleFilter = ({
  sectionKey,
  optionKey,
  userAppliedFilterKeys,
  diaryFilterSections,
  setUserAppliedFilterKeys
}: IToggleFilter) => {
  const currentFiltersOnSection = new Set(userAppliedFilterKeys[sectionKey]);
  const sectionDetails = diaryFilterSections.find(
    (section) => sectionKey === section.key
  );
  const action = currentFiltersOnSection.has(optionKey) ? "remove" : "add";

  if (action === "add" && sectionDetails?.maxSelections === 1) {
    currentFiltersOnSection.clear();
  }

  if (currentFiltersOnSection.has(optionKey)) {
    currentFiltersOnSection.delete(optionKey);
  } else {
    currentFiltersOnSection.add(optionKey);
  }

  setUserAppliedFilterKeys({
    ...userAppliedFilterKeys,
    [sectionKey]: currentFiltersOnSection
  });
};

interface IApplyFilter {
  userAppliedFilterKeys: IUserAppliedFilterKeys;
  setSelectedFilters: React.Dispatch<React.SetStateAction<ISelectedFilters>>;
  closeModal?: () => void;
}
export const ApplyFilter = ({
  userAppliedFilterKeys,
  setSelectedFilters,
  closeModal
}: IApplyFilter) => {
  const bookingTypes = Array.from(
    userAppliedFilterKeys[FilterSectionKeys.bookingTypes] ?? []
  );
  const bookingZones = Array.from(
    userAppliedFilterKeys[FilterSectionKeys.bookingZones] ?? []
  );
  const bookingAttributes = Array.from(
    userAppliedFilterKeys[FilterSectionKeys.bookingAttributes] ?? []
  );
  const timeOfDay = Array.from(
    userAppliedFilterKeys[FilterSectionKeys.timeOfDay] ?? []
  );
  const count =
    bookingTypes.length +
    bookingZones.length +
    bookingAttributes.length +
    timeOfDay.length;

  setSelectedFilters({
    bookingAttributes,
    bookingTypes,
    bookingZones,
    timeOfDay,
    count
  });

  if (closeModal) {
    closeModal();
  }
};
